/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { ITableSortConfig, TABLE_SORT } from "./TableTypes";
import { isFunction } from "@adapptio/lib-shared";

// eslint-disable-next-line @typescript-eslint/ban-types
export type TSortableTableDataElement = {};
export type TSortableTableData = TSortableTableDataElement[];

const compare = (
	a: TSortableTableDataElement,
	b: TSortableTableDataElement,
	sort: ITableSortConfig,
	getValue?: (item: TSortableTableDataElement, columnId: ITableSortConfig["columnId"]) => unknown
) => {
	const valA = isFunction(getValue) ? getValue(a, sort.columnId) : a[sort.columnId];
	const valB = isFunction(getValue) ? getValue(b, sort.columnId) : b[sort.columnId];

	const greater = sort.direction === TABLE_SORT.ASC ? 1 : -1;
	const lower = sort.direction === TABLE_SORT.ASC ? -1 : 1;

	if (valA !== undefined && valB !== undefined) {
		return valA > valB ? greater : valA < valB ? lower : 0;
	} else if (valA !== undefined) {
		return greater;
	} else if (valB !== undefined) {
		return lower;
	} else {
		return 0;
	}
};

/**
 * Returns sorted table data
 * Is supported on array of objects were property name matches sort columnId.
 *
 * @param data Table data
 * @param sort Sort config
 */
export function sortTableData<TData extends TSortableTableData>(
	data: TData,
	sort: ITableSortConfig[],
	getValue?: (item: TData[number], columnId: ITableSortConfig["columnId"]) => unknown
): TData {
	if (sort.length === 0) {
		return data;
	}

	return data.slice().sort((a, b) => {
		let res = 0;

		for (let i = 0; i < sort.length; i++) {
			res = compare(a, b, sort[i], getValue);

			if (res !== 0) {
				return res;
			}
		}

		return res;
	}) as TData;
}
